import React, {useEffect, useRef, useState} from "react"
import {useTranslation} from "react-i18next"
import {ArrowLeftIcon, ArrowRightIcon} from "@heroicons/react/20/solid"
import {Swiper as SwiperType} from "swiper"
import {Swiper, SwiperProps, SwiperSlide} from "swiper/react"
import {twMerge} from "tailwind-merge"

import "swiper/css"

type NavigationButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  direction: "prev" | "next"
}

const NavigationButton: React.FC<NavigationButtonProps> = ({direction, ...props}) => {
  const Icon = direction === "prev" ? ArrowLeftIcon : ArrowRightIcon

  return (
    <button
      className={twMerge(
        "pointer-events-auto flex h-10 w-10 items-center justify-center rounded-full transition-colors",
        direction === "prev" ? "-translate-x-1/2" : "translate-x-1/2",
        props.disabled ? "bg-gray-200 text-white" : "bg-cr-blue text-white"
      )}
      {...props}
    >
      <Icon className={"h-7 w-7"} />
    </button>
  )
}

export const Carousel: React.FC<{
  children: React.ReactNode[]
  swiperProps?: SwiperProps
  className?: string
}> = ({children, swiperProps, className}) => {
  const swiperRef = useRef<SwiperType>(null)
  const slides = React.Children.toArray(children)
  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(false)
  const [showNavigation, setShowNavigation] = useState(false)
  const {t} = useTranslation()

  const updateNavigationState = React.useCallback(() => {
    if (swiperRef.current) {
      setIsBeginning(swiperRef.current.isBeginning)
      setIsEnd(swiperRef.current.isEnd)

      // Check if navigation is needed based on total slides and visible slides
      const totalSlides = slides.length
      const visibleSlides = swiperRef.current.params.slidesPerView as number
      setShowNavigation(totalSlides > visibleSlides)
    }
  }, [slides.length])

  useEffect(() => {
    updateNavigationState()

    // Update navigation visibility on resize
    const handleResize = () => {
      updateNavigationState()
    }

    window.addEventListener("resize", handleResize)
    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [slides.length, updateNavigationState])

  const handlePrev = () => {
    swiperRef.current?.slidePrev()
    updateNavigationState()
  }

  const handleNext = () => {
    swiperRef.current?.slideNext()
    updateNavigationState()
  }

  return (
    <div className={twMerge("relative w-full", className)}>
      <Swiper
        onBeforeInit={swiper => {
          swiperRef.current = swiper
        }}
        spaceBetween={16}
        slidesPerView={1}
        // onSlideChange={() => console.log("slide change")}
        // onSwiper={swiper => console.log(swiper)}
        breakpoints={{
          460: {
            slidesPerView: 2,
          },
          640: {
            slidesPerView: 3,
          },
          1024: {
            slidesPerView: 4,
          },
        }}
        onSlideChange={updateNavigationState}
        onBreakpoint={updateNavigationState}
        onResize={updateNavigationState}
        onAfterInit={updateNavigationState}
        {...swiperProps}
      >
        {slides.map((child, index) => (
          <SwiperSlide key={index}>{child}</SwiperSlide>
        ))}
      </Swiper>

      {showNavigation && (
        <div className={"pointer-events-none absolute top-1/2 z-20 flex w-full -translate-y-1/2 justify-between"}>
          <NavigationButton
            onClick={handlePrev}
            disabled={isBeginning}
            aria-label={t("Carousel_PreviousSlide")}
            direction={"prev"}
          />
          <NavigationButton
            onClick={handleNext}
            disabled={isEnd}
            aria-label={t("Carousel_NextSlide")}
            direction={"next"}
          />
        </div>
      )}

      {showNavigation && !isEnd && (
        <div
          className={
            "pointer-events-none invisible absolute top-0 right-0 z-10 h-full w-50 bg-gradient-to-r from-transparent to-white lg:visible"
          }
        />
      )}

      {showNavigation && !isBeginning && (
        <div
          className={
            "pointer-events-none invisible absolute top-0 left-0 z-10 h-full w-50 bg-gradient-to-l from-transparent to-white lg:visible"
          }
        />
      )}
    </div>
  )
}
